import { useCallback, useEffect, useMemo, useState } from "react";
import { IoIosTrash } from "react-icons/io";
import { useAuth } from "../context/authContext";

const UPLOAD_API_URL = "https://ideiperu.com/proyectos/panelHudbay/upload.php";
const HISTORY_API_URL = "https://ideiperu.com/proyectos/panelHudbay/list_uploads.php";
const DELETE_API_URL = "https://ideiperu.com/proyectos/panelHudbay/delete.php";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("es-PE");
};

const normalizeHistory = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((item, index) => ({
    id: item.id || item.ID || item.uuid || `row-${index}`,
    uploadedAt:
      item.uploaded_at ||
      item.uploadedAt ||
      item.created_at ||
      item.createdAt ||
      "",
    fileName:
      item.original_name ||
      item.original_dxf_name ||
      item.original_pdf_name ||
      item.file_name ||
      item.dxf_name ||
      item.pdf_name ||
      "-",
    fileUrl:
      item.file_url ||
      item.dxf_url ||
      item.pdf_url ||
      item.url_dxf ||
      item.url_pdf ||
      "",
    category: item.category || item.type || "-",
    uploadedBy:
      item.uploaded_by || item.uploadedBy || item.usuario || item.user || "-",
  }));
};

const UploadFiles = () => {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("diario");
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const { fetchAuth } = useAuth();

  // eliminar
  const [confirmDelete, setConfirmDelete] = useState(null); // item a eliminar
  const [loadingDelete, setLoadingDelete] = useState(false);

  const apisReady = useMemo(() => Boolean(UPLOAD_API_URL && HISTORY_API_URL), []);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const loadHistory = useCallback(async () => {
    if (!HISTORY_API_URL) return;

    setLoadingHistory(true);
    try {
      const response = await fetchAuth(HISTORY_API_URL, {
        method: "GET",
      });
      const data = await response.json();
      const historyRows = Array.isArray(data)
        ? data
        : data.data || data.items || [];
      setHistory(normalizeHistory(historyRows));
    } catch (err) {
      setError("No se pudo cargar el historial de archivos.");
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const onSubmit = async (event) => {
    event.preventDefault();
    clearMessages();

    if (!UPLOAD_API_URL) {
      setError(
        "Falta configurar REACT_APP_UPLOAD_API_URL con la URL del script PHP."
      );
      return;
    }

    if (!file) {
      setError('Debes seleccionar un archivo "PDF".');
      return;
    }

    const isPdf = file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      setError("Formato invalido. Solo se permite .pdf.");
      return;
    }

    setLoadingUpload(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", category);

      const response = await fetchAuth(UPLOAD_API_URL, {
        method: "POST",
        body: formData,
      });
      const payload = await response.json();

      if (!response.ok || payload.success === false) {
        throw new Error(payload.message || "No se pudo subir los archivos.");
      }

      setSuccess(payload.message || "Archivos subidos correctamente.");
      setFile(null);
      event.target.reset();
      loadHistory();
    } catch (err) {
      setError(err.message || "Ocurrio un error al subir los archivos.");
    } finally {
      setLoadingUpload(false);
    }
  };

  const requestDelete = (item) => {
    clearMessages();
    setConfirmDelete(item);
  };

  const cancelDelete = () => {
    setConfirmDelete(null);
  };

  const doDelete = async () => {
    console.log(confirmDelete);
    console.log(confirmDelete?.id);

    if (!confirmDelete?.id) return;

    if (!DELETE_API_URL) {
      setError("Falta configurar la URL del endpoint de borrado (DELETE_API_URL).");
      setConfirmDelete(null);
      return;
    }

    setLoadingDelete(true);
    clearMessages();

    try {
      const url = new URL(DELETE_API_URL);
      url.searchParams.set("id", String(confirmDelete.id));

      const response = await fetchAuth(url.toString(), {
        method: "DELETE",
      });

      // Si tu backend responde JSON
      const payload = await response.json().catch(() => ({}));

      if (!response.ok || payload.success === false) {
        throw new Error(payload.message || "No se pudo eliminar el archivo.");
      }

      setSuccess(payload.message || "Archivo eliminado correctamente.");
      setConfirmDelete(null);
      loadHistory();
    } catch (err) {
      setError(err.message || "Ocurrió un error al eliminar.");
    } finally {
      setLoadingDelete(false);
    }
  };

  return (
    <div className="w-full h-full flex flex-col gap-6 bg-[#f9f9f9] px-4 py-8 overflow-auto">
      <section className="w-full bg-white rounded-xl border border-[#dfdfdf] shadow-sm p-5">
        <h2 className="font-Poppins font-semibold text-[#2e354d] text-[1.4rem] uppercase">
          Carga de Archivos
        </h2>
        <p className="font-Poppins text-[#4f566d] text-[0.9rem] mt-1">
          Seleccione el tipo de archivo que subirá.
        </p>

        {!apisReady && (
          <div className="mt-4 rounded-lg bg-[#fff3cd] border border-[#ffe49a] p-3 text-[#7c5f00] text-sm font-Poppins">
            Configura en tus rutas `.
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-lg bg-[#ffefef] border border-[#ffc9c9] p-3 text-[#8a2222] text-sm font-Poppins">
            {error}
          </div>
        )}
        {success && (
          <div className="mt-4 rounded-lg bg-[#effff0] border border-[#b8f1bc] p-3 text-[#1e6d25] text-sm font-Poppins">
            {success}
          </div>
        )}

        <form className="mt-5 grid gap-4" onSubmit={onSubmit}>
          <div className="grid grid-cols-1 min-[860px]:grid-cols-2 gap-4">
            <label className="font-Poppins text-[#2e354d] text-[0.92rem]">
              Tipo de archivo
              <select
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                className="w-full mt-2 rounded-lg border border-[#cfd4e2] bg-[#fefefe] p-2 text-[#2e354d]"
              >
                <option value="diario">Radio de influencia</option>
                <option value="semanal">Plano de Vias</option>
              </select>
            </label>

            <label className="font-Poppins text-[#2e354d] text-[0.92rem]">
              Archivo PDF
              <input
                type="file"
                accept=".pdf"
                onChange={(event) => setFile(event.target.files?.[0] || null)}
                className="w-full mt-2 rounded-lg border border-[#cfd4e2] bg-[#fefefe] p-2 text-[#2e354d]"
              />
            </label>
          </div>

          <div>
            <button
              type="submit"
              disabled={loadingUpload}
              className="font-Poppins font-semibold bg-[#2e354d] text-white rounded-lg px-5 py-2 hover:bg-[#22293f] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loadingUpload ? "Subiendo..." : "Subir archivos"}
            </button>
          </div>
        </form>
      </section>

      <section className="w-full bg-white rounded-xl border border-[#dfdfdf] shadow-sm p-5">
        <div className="flex items-center justify-between gap-4">
          <h3 className="font-Poppins font-semibold text-[#2e354d] text-[1.4rem] uppercase">
            Historial de Archivos
          </h3>
          <button
            className="font-Poppins text-sm px-3 py-1.5 rounded-md border border-[#cfd4e2] text-[#2e354d] hover:bg-[#f4f6fb]"
            onClick={loadHistory}
            type="button"
          >
            Recargar
          </button>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse">
            <thead>
              <tr className="bg-[#f0f2f7] text-left">
                <th className="p-2 font-Poppins text-[#2e354d] text-sm">Fecha</th>
                <th className="p-2 font-Poppins text-[#2e354d] text-sm">Archivo</th>
                <th className="p-2 font-Poppins text-[#2e354d] text-sm w-[90px]">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {loadingHistory && (
                <tr>
                  <td
                    className="p-3 text-sm font-Poppins text-[#59607a] border-b border-[#eceef4]"
                    colSpan={3}
                  >
                    Cargando historial...
                  </td>
                </tr>
              )}

              {!loadingHistory && history.length === 0 && (
                <tr>
                  <td
                    className="p-3 text-sm font-Poppins text-[#59607a] border-b border-[#eceef4]"
                    colSpan={3}
                  >
                    No hay archivos registrados.
                  </td>
                </tr>
              )}

              {!loadingHistory &&
                history.map((item) => (
                  <tr key={item.id} className="border-b border-[#eceef4]">
                    <td className="p-2 text-sm font-Poppins text-[#2f3444]">
                      {formatDate(item.uploadedAt)}
                    </td>
                    <td className="p-2 text-sm font-Poppins text-[#2f3444]">
                      {item.fileUrl ? (
                        <a
                          href={item.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#2352b7] hover:underline"
                        >
                          {item.fileName}
                        </a>
                      ) : (
                        item.fileName
                      )}
                    </td>
                    <td className="p-2">
                      <button
                        type="button"
                        onClick={() => requestDelete(item)}
                        className="inline-flex items-center justify-center rounded-md border border-[#cfd4e2] px-1py-1 text-[#8a2222] hover:bg-[#ffefef]"
                        title="Eliminar"
                        aria-label={`Eliminar ${item.fileName}`}
                      >
                        <IoIosTrash size={"2rem"} />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-black/40"
            onClick={loadingDelete ? undefined : cancelDelete}
          />
          <div className="relative w-full max-w-md rounded-xl bg-white border border-[#dfdfdf] shadow-lg p-5">
            <h4 className="font-Poppins font-semibold text-[#2e354d] text-[1.5rem] text-center">
              CONFIRMAR ELIMINACIÓN
            </h4>
            <p className="mt-2 font-Poppins text-[#813030] text-sm">
              ¿Seguro que deseas eliminar este archivo?
            </p>

            <div className="mt-3 rounded-lg bg-[#f4f6fb] border border-[#e6e9f2] p-3">
              <div className="font-Poppins text-sm text-[#2f3444]">
                <span className="font-semibold">Archivo:</span>{" "}
                {confirmDelete.fileName}
              </div>
              <div className="font-Poppins text-xs text-[#59607a] mt-1">
                {formatDate(confirmDelete.uploadedAt)}
              </div>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={cancelDelete}
                disabled={loadingDelete}
                className="font-Poppins text-sm px-4 py-2 rounded-lg border border-[#cfd4e2] text-[#2e354d] hover:bg-[#f4f6fb] disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={doDelete}
                disabled={loadingDelete}
                className="font-Poppins text-sm px-4 py-2 rounded-lg bg-[#8a2222] text-white hover:bg-[#741c1c] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loadingDelete ? "Eliminando..." : "Sí, eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadFiles;